import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../api';
import { t } from '../../i18n';
import { fmtDate, fmtKB, fmtTime } from '../../lib/format';
import { folderLabel } from '../../lib/ordner';
import { useApi } from '../../lib/useApi';
import { Preview } from '../belege/BelegSheet';
import { Sheet } from '../belege/Sheet';
import { useCloseSheet } from '../lohn/Pages';
import { SignForm } from './SignForm';
import type { DocDetail } from './types';
import { ShareFile } from '../../components/ShareFile';

// Ein Bescheid, Dokument oder Teil des Jahresabschlusses von der Kanzlei (?dok=<id>): Vorschau und Herunterladen.
// Dokumente zum Unterschreiben: unter der Vorschau „Jetzt unterschreiben“; danach zeigt das Fenster das unterschriebene
// PDF (Original plus Unterschriftsblatt).
export function DokSheetHost() {
  const [params] = useSearchParams();
  const close = useCloseSheet('dok');
  const id = params.get('dok');
  return id ? <DokSheet key={id} id={id} onClose={close} /> : null;
}

function DokSheet({ id, onClose }: { id: string; onClose(): void }) {
  const res = useApi<DocDetail>(`/api/app/doc/${id}`, { live: ['docs'] });
  const [signing, setSigning] = useState(false);
  const d = res.data;

  // Meldung dazu gilt als gelesen, sobald das Fenster die Datei zeigt
  const loaded = !!d;
  useEffect(() => {
    if (loaded) api.post(`/api/app/doc/${id}/read`).catch(() => {});
  }, [id, loaded]);

  if (!d) {
    return (
      <Sheet title={t('Von Ihrer Kanzlei')} onClose={onClose}>
        {res.error ? <p className="meta">{res.error}</p> : <div className="spinner" aria-label={t('Lädt')} />}
      </Sheet>
    );
  }

  const original = `/api/app/doc/${d.id}/file`;
  const signed = d.sign?.signedAt && d.sign.file ? { at: d.sign.signedAt, by: d.sign.signerName, file: d.sign.file } : null;
  const url = signed ? `/api/app/doc/${d.id}/signed` : original;
  const file = signed ? signed.file : d.file;
  return (
    <Sheet title={d.title} onClose={onClose}>
      <div className="meta">
        {d.clientName} ·{' '}
        {d.kind === 'bescheid'
          ? t('Steuerbescheid')
          : d.kind === 'jahresabschluss'
            ? t('Jahresabschluss {year}', { year: String(d.year) })
            : `${t('Dokumente')} › ${t(folderLabel(d.folder))}`}{' '}
        · {t('hochgeladen von Ihrer Kanzlei am {date}', { date: fmtDate(d.uploadedAt) })}
      </div>
      {signed && (
        <p className="dok-done" role="status">
          ✓{' '}
          {t('Unterschrieben von {name} am {date} um {time} Uhr', {
            name: signed.by ?? '',
            date: fmtDate(signed.at),
            time: fmtTime(signed.at),
          })}
        </p>
      )}
      <Preview key={url} url={url} mime={file.mime} name={file.name} />
      {d.sign && !signed ? (
        signing ? (
          <SignForm
            docId={d.id}
            onCancel={() => setSigning(false)}
            onDone={() => {
              setSigning(false);
              void res.reload();
            }}
          />
        ) : (
          <div className="dok-ask" role="status">
            <b>✍️ {t('Bitte unterschreiben')}</b>
            <span>{t('Ihre Kanzlei bittet Sie, dieses Dokument zu unterschreiben. Bitte lesen Sie es vorher ganz durch.')}</span>
            <div className="row">
              <button type="button" className="btn2 dark" onClick={() => setSigning(true)}>
                {t('Jetzt unterschreiben')}
              </button>
              <a className="btn2" href={`${original}?download=1`} download>
                {d.file.mime === 'application/pdf' ? t('PDF herunterladen') : t('Bild herunterladen')}
              </a>
              <ShareFile url={`${original}?download=1`} name={d.file.name} />
            </div>
          </div>
        )
      ) : (
        <div className="row dok-row">
          <a className="btn2 dark" href={`${url}?download=1`} download>
            {signed
              ? t('Unterschriebenes PDF herunterladen')
              : d.file.mime === 'application/pdf'
                ? t('PDF herunterladen')
                : t('Bild herunterladen')}
          </a>
          <ShareFile url={`${url}?download=1`} name={file.name} />
        </div>
      )}
      <p className="src">
        {file.name} · {fmtKB(file.size)}
      </p>
    </Sheet>
  );
}
